<!-- eslint-disable vue/no-v-html -->
<template>
	<MainContainer
		header-selected="blog"
		:loading="status == 'pending'"
		:fetched="status == 'success'"
		:error="error"
		no-company-branding
		class="bg-[#fafcfd]"
		@try-again="refresh"
	>
		<BlogBreadcrumb
			:links="[
				{
					name: $t('home'),
					to: $routeUrl.HomeUrl()
				},
				{
					name: $t('blog'),
					to: $routeUrl.BlogUrl()
				},
				category
					? {
							name: $stringUtil.getLocalText(category?.title) || '',
							to: $routeUrl.BlogCategoryUrl(category?.id)
						}
					: null,
				data?.article
					? {
							name: $stringUtil.getLocalText(data?.article?.title) || '',
							to: ''
						}
					: null
			]"
		/>
		<BlogSingleHeroSection :article="data?.article" />
		<div class="mb-20 mt-20 flex flex-col items-center px-4 lg:mb-32 lg:px-6">
			<div class="grid w-full max-w-screen-xl grid-cols-1 gap-x-16 gap-y-20 lg:grid-cols-12">
				<main class="col-span-1 flex flex-col lg:col-span-8">
					<PostSlider
						v-if="data?.article?.covers?.length || data?.article?.videos?.length"
						:post="data?.article"
						class="mb-12"
					/>
					<div
						class="prose prose-base prose-slate max-w-none sm:prose-lg prose-headings:scroll-mt-24 prose-a:text-primary hover:prose-a:text-primary-dark prose-img:rounded-xl prose-img:shadow-lg"
						v-html="$stringUtil.getLocalText(data?.article?.content)"
					></div>
					<div
						v-if="data?.article?.keywords?.length"
						class="border-slate-200 mt-12 flex flex-wrap items-center gap-3 border-t pt-8"
					>
						<span class="text-slate-700 font-semibold">{{ $t("keywords") }}:</span>
						<NuxtLink
							v-for="keyword in data.article?.keywords"
							:key="keyword.slug"
							:to="$routeUrl.BlogKeywordUrl(keyword.slug)"
							class="text-slate-700 bg-slate-100 rounded-full px-4 py-2 text-sm font-medium transition-all hover:text-white hover:bg-primary"
						>
							<span> {{ keyword.title }} </span>
						</NuxtLink>
					</div>
				</main>
				<aside class="col-span-1 flex flex-col lg:col-span-4">
					<div class="sticky top-24 space-y-8">
						<!-- Related -->
						<div class="bg-white rounded-2xl p-6 shadow-lg">
							<h3 class="text-slate-900 mb-6 flex items-center gap-2 text-xl font-bold">
								<svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M13 10V3L4 14h7v7l9-11h-7z"
									/>
								</svg>
								{{ $t("related_articles") }}
							</h3>
							<div class="flex flex-col space-y-4">
								<NuxtLink
									v-for="p in data?.related_post_data?.posts"
									:key="p.id"
									:to="$routeUrl.BlogArticleUrl(p.id)"
								>
									<ArticleCard
										:article="p"
										mode="h"
										size="sm"
										hide-footer
										hide-category
										:article-url="$routeUrl.BlogArticleUrl(p.id)"
									/>
								</NuxtLink>

								<div
									v-if="!data?.related_post_data?.posts?.length"
									class="text-slate-500 py-8 text-center text-sm"
								>
									{{ $t("nothing_found") }}
								</div>
							</div>
						</div>
						<!-- Most Visited -->
						<div class="bg-white rounded-2xl p-6 shadow-lg">
							<h3 class="text-slate-900 mb-6 flex items-center gap-2 text-xl font-bold">
								<svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
									/>
								</svg>
								{{ $t("most_visited") }}
							</h3>
							<div class="flex flex-col space-y-4">
								<NuxtLink
									v-for="p in data?.most_popular_post_data?.posts"
									:key="p.id"
									:to="$routeUrl.BlogArticleUrl(p.id)"
								>
									<ArticleCard
										:article="p"
										mode="h"
										size="sm"
										hide-footer
										hide-category
										:article-url="$routeUrl.BlogArticleUrl(p.id)"
									/>
								</NuxtLink>

								<div
									v-if="!data?.most_popular_post_data?.posts?.length"
									class="text-slate-500 py-8 text-center text-sm"
								>
									{{ $t("nothing_found") }}
								</div>
							</div>
						</div>
					</div>
				</aside>
			</div>
		</div>
		<BlogCategoriesSection
			:categories="categories"
			class="border-slate-300 border-b !from-slate-200 !to-slate-500"
		/>
	</MainContainer>
</template>

<script lang="ts" setup>
const cmsStore = useCmsStore()
const app = useNuxtApp()
const route = useRoute()
const categoryStore = useCategoryStore()

const articleId = computed(() => Number(route.params.articleId))

const { data, status, error, refresh } = useLazyAsyncData(
	String(route.path),
	() =>
		cmsStore.contentBundle({
			company_id: null,
			queries: [
				{
					query_name: "article" as const,
					query_type: "post",
					types: "article",
					id: articleId.value,
					view: true
				},
				{
					query_name: "related_post_data" as const,
					query_type: "posts",
					types: "article",
					page: 1,
					limit: 5,
					order_by: "published",
					direction: "desc",
					has_image: true,
					category_ids: "$article->category_ids"
				},
				{
					query_name: "most_popular_post_data" as const,
					query_type: "posts",
					types: "article",
					page: 1,
					limit: 5,
					order_by: "view_count",
					direction: "desc",
					has_image: true
				}
			]
		}),
	{
		watch: [() => route.query]
	}
)

const categories = computed(() => categoryStore.categories?.filter((v) => v.type == "article") || [])

const category = computed(() => categoryStore.categories?.find((v) => data.value?.article?.category_ids.includes(v.id)))

useHead(() => ({
	title: data.value?.article?.id
		? app.$stringUtil.getLocalText(data.value?.article?.title) + " | " + app.$i18n.t("app_blog")
		: app.$i18n.t("app_blog") + " | " + app.$i18n.t("app_motto")
}))
</script>
